import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import {
  AISkeletonText, Button, IconButton, InlineNotification, SkeletonText, StructuredListBody, StructuredListCell, StructuredListRow,
  StructuredListWrapper, Tag, TextArea, Tile, UnorderedList, ListItem,
} from '@carbon/react';
import { Checkmark, Close, Launch, Renew, SendFilled, StopFilledAlt, WarningAlt } from '@carbon/icons-react';

import type { AcaoNaConversa, DadosDaConversa, EstadoDaAcao } from '../../../logica/nyta/useConversaDaNyta';
import { partesDaFala } from '@maestra/core/nucleo/arquivosNaFala';
import type { AnexoDaNyta } from '@maestra/core/nucleo/graficoDaConversa';
import { sobreDaAcao, sobreDaResposta } from '../../../logica/nyta/explicacoes';
import { MarcaDaNyta } from '../../marcas/MarcaDaNyta';
import { RotuloDeIA } from '../../padroes/ia/RotuloDeIA';
import { ROTULOS, rotuloDoAviso } from '../../padroes/rotulos';
import { FalaDaNyta } from './PartesDaFala';
import styles from './Nyta.module.scss';

// A CONVERSA COM A NYTA (Carbon for AI), a mesma na página e no painel flutuante:
// - a pergunta de quem escreve é a bolha (`chat-bubble-user`), à direita;
// - a resposta da Nyta é texto na coluna de leitura, com o nome dela e o RÓTULO DE IA focado em cada
//   resposta (a explicação diz que é IA, o que ela lê e que pode errar);
// - a ação que ela propõe é um Tile com presença de IA: o que vai ser feito, Confirmar e Cancelar;
// - enquanto ela pensa, o esqueleto de IA do Carbon e o que ela está fazendo ("Buscando na internet");
// - o campo embaixo, com o enviar que vira parar enquanto ela escreve.

/**
 * A conversa e o que a demonstração da landing acrescenta: `rascunho` é o texto que a pessoa "está
 * digitando" no campo (a demonstração escreve letra por letra), e `assumir` é chamado quando quem
 * visita digita de verdade, para a demonstração soltar o campo. Na plataforma, os dois ficam de fora.
 */
type ComDemonstracao = DadosDaConversa & { rascunho?: string; assumir?: (texto: string) => void };

const LIMITE_DO_TEXTO = 1000;
const AVISAR_A_PARTIR_DE = LIMITE_DO_TEXTO - 150;
const TEMPO_DA_ACAO = 30_000;

// O rótulo do cartão acompanha o estado: "quer fazer" só enquanto a decisão é da pessoa.
const ROTULO_DO_ESTADO: Record<EstadoDaAcao, string> = {
  pendente: 'A Nyta quer fazer', executando: 'A Nyta está fazendo', confirmada: 'Você confirmou',
  feita: 'A Nyta fez', cancelada: 'Você cancelou', falhou: 'A Nyta não conseguiu fazer',
};

/** A explicação da resposta, pelo que ela tem (gráfico e a fonte dele, arquivo, tabela). */
const explicacaoDa = (texto: string, anexos: Record<string, AnexoDaNyta> | undefined, leituras: string[] | null) => {
  const partes = partesDaFala(texto);
  const fontesDosGraficos = Array.from(new Set(partes.flatMap((p) => (p.tipo === 'grafico' && anexos?.[p.id] ? [anexos[p.id].grafico.fonte] : []))));
  return sobreDaResposta({ leituras, fontesDosGraficos, temArquivo: partes.some((p) => p.tipo === 'arquivo'), temTabela: partes.some((p) => p.tipo === 'tabela') });
};

const TAG_DO_ESTADO: Record<EstadoDaAcao, { tipo: 'blue' | 'green' | 'gray' | 'red' | 'cool-gray'; texto: string } | null> = {
  pendente: null,
  executando: { tipo: 'blue', texto: 'Executando' },
  confirmada: { tipo: 'cool-gray', texto: 'Confirmada' },
  feita: { tipo: 'green', texto: 'Feita' },
  cancelada: { tipo: 'gray', texto: 'Cancelada' },
  falhou: { tipo: 'red', texto: 'Não foi feita' },
};

/** A ação que a Nyta propôs: o que vai ser feito, e a decisão de quem conversa. */
const CartaoDaAcao: FC<{ acao: AcaoNaConversa }> = ({ acao }) => {
  // Confirmada e sem resposta em 30 s: diz que demorou, e deixa tentar de novo.
  const [demorou, setDemorou] = useState(false);
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (relogio.current) clearTimeout(relogio.current); }, []);
  useEffect(() => {
    if (acao.estado !== 'executando' && relogio.current) { clearTimeout(relogio.current); relogio.current = null; }
    if (acao.estado !== 'executando') setDemorou(false);
  }, [acao.estado]);
  const confirmar = () => {
    setDemorou(false);
    relogio.current = setTimeout(() => setDemorou(true), TEMPO_DA_ACAO);
    acao.confirmar?.();
  };
  const tag = TAG_DO_ESTADO[acao.estado];
  const decide = !!acao.confirmar && (acao.estado === 'pendente' || acao.estado === 'falhou');
  return (
    // A presença de IA do Carbon: o Tile com o rótulo no canto ganha o estilo de IA.
    <Tile className={styles.acao} decorator={(
      <RotuloDeIA sobre={sobreDaAcao({
        ferramenta: acao.ferramenta,
        estado: acao.estado,
        acoes: acao.abrirModulo ? <Button kind='ghost' size='sm' renderIcon={Launch} onClick={acao.abrirModulo}>{`Abrir ${acao.modulo}`}</Button> : undefined,
      })} />
    )}>
      <div className={styles.cabecaDaAcao}>
        <p className={styles.rotuloDaAcao}>{ROTULO_DO_ESTADO[acao.estado]}</p>
        <h3 className={styles.tituloDaAcao}>{acao.titulo}</h3>
        {tag && <Tag type={tag.tipo} size='sm' className={styles.tagDaAcao}>{tag.texto}</Tag>}
      </div>
      {acao.campos.length > 0 && (
        <StructuredListWrapper isCondensed aria-label={`O que ${acao.titulo.toLowerCase()} vai gravar`} className={styles.camposDaAcao}>
          <StructuredListBody>
            {acao.campos.map((c) => (
              <StructuredListRow key={c.rotulo}>
                <StructuredListCell className={styles.rotuloDoCampo}>{c.rotulo}</StructuredListCell>
                <StructuredListCell className={styles.valorDoCampo}>
                  {c.lista ? <UnorderedList>{c.lista.map((i) => <ListItem key={i}>{i}</ListItem>)}</UnorderedList> : c.valor}
                </StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
      )}
      {(acao.estado === 'falhou' || demorou) && (
        <p className={styles.erroDaAcao}>
          <WarningAlt aria-hidden />
          {demorou ? 'A ação está demorando. Tente de novo.' : 'Não foi possível completar a ação. Tente de novo.'}
        </p>
      )}
      {(decide || demorou) && acao.confirmar && (
        <div className={styles.botoesDaAcao}>
          <Button kind='secondary' size='md' renderIcon={Close} onClick={acao.cancelar}>Cancelar</Button>
          <Button kind='primary' size='md' renderIcon={acao.estado === 'falhou' || demorou ? Renew : Checkmark} onClick={confirmar}>{acao.estado === 'falhou' || demorou ? 'Tentar de novo' : 'Confirmar'}</Button>
        </div>
      )}
    </Tile>
  );
};

const Pensando: FC<{ texto: string }> = ({ texto }) => (
  <div className={styles.nyta}>
    <div className={styles.quemFala}>
      <span className={styles.marca}><MarcaDaNyta pensando /></span>
      <span className={styles.nome}>Nyta</span>
      <span className={styles.apoio}>{texto}</span>
    </div>
    <AISkeletonText paragraph lineCount={2} className={styles.esqueleto} />
  </div>
);

/** O campo: Enter envia, Shift+Enter quebra a linha; enquanto ela escreve, enviar vira parar. */
const Campo: FC<{ c: ComDemonstracao; destaque?: boolean }> = ({ c, destaque }) => {
  const [digitado, setTexto] = useState('');
  const texto = c.rascunho ?? digitado;
  const enviar = useCallback(() => {
    if (!texto.trim() || !c.podeEscrever) return;
    void c.enviar(texto);
    setTexto('');
  }, [texto, c]);

  if (c.limite) {
    return (
      <div className={styles.limite} role='status'>
        <p className={styles.tituloDoLimite}>{`Você usou as ${c.limite.usadas} mensagens de hoje.`}</p>
        <p className={styles.apoio}>
          {`A Nyta volta ${c.limite.volta}`}
          <span className={styles.numero} aria-hidden>{` · ${c.limite.cronometro}`}</span>
        </p>
      </div>
    );
  }
  const linhas = Math.min(6, Math.max(destaque ? 2 : 1, texto.split('\n').length));
  const apoio = c.esperandoDecisao
    ? 'Confirme ou cancele a ação acima para continuar.'
    : texto.length >= AVISAR_A_PARTIR_DE ? `${texto.length} de ${LIMITE_DO_TEXTO} caracteres · ${c.ressalva}`
      : c.uso ? `${c.ressalva} ${c.uso.usadas} de ${c.uso.limite} mensagens hoje.` : c.ressalva;
  return (
    <div className={styles.campo}>
      <div className={styles.caixaDoCampo}>
        <TextArea
          id={`nyta-campo-${destaque ? 'vazia' : 'conversa'}`}
          labelText='Mensagem para a Nyta'
          hideLabel
          rows={linhas}
          maxLength={LIMITE_DO_TEXTO}
          value={texto}
          placeholder={c.convite}
          disabled={!c.podeEscrever && !c.escrevendo}
          onChange={(e) => {
            const novo = e.target.value.slice(0, LIMITE_DO_TEXTO);
            setTexto(novo);
            if (c.rascunho !== undefined) c.assumir?.(novo);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
          className={styles.textoDoCampo}
        />
        {c.escrevendo && c.parar
          ? <IconButton kind='secondary' size='md' label='Parar a resposta' onClick={c.parar} className={styles.botaoDoCampo}><StopFilledAlt /></IconButton>
          : <IconButton kind='primary' size='md' label='Enviar' disabled={!texto.trim() || !c.podeEscrever} onClick={enviar} className={styles.botaoDoCampo}><SendFilled /></IconButton>}
      </div>
      <p className={styles.ressalva}>{apoio}</p>
    </div>
  );
};

export const Conversa: FC<{ c: ComDemonstracao; compacta?: boolean }> = ({ c, compacta }) => {
  const rolagem = useRef<HTMLDivElement>(null);
  const topo = useRef<HTMLDivElement>(null);
  const subiu = useRef(false);
  const primeira = useRef(true);
  const alturaAntes = useRef(0);
  // Dispensar vale para ESTE aviso: outro módulo fora do ar, depois, aparece.
  const [modulosDispensados, setModulosDispensados] = useState<string | null>(null);
  // O log fica mudo enquanto o histórico entra por cima, e volta a falar no quadro SEGUINTE ao que
  // pôs as mensagens antigas (o núcleo insere e desliga o "carregando" no mesmo commit).
  const [logMudo, setLogMudo] = useState(false);
  useEffect(() => {
    if (c.carregandoHistorico) { setLogMudo(true); return undefined; }
    const id = requestAnimationFrame(() => setLogMudo(false));
    return () => cancelAnimationFrame(id);
  }, [c.carregandoHistorico, c.fio.length]);

  // Abrir cai na mais recente; quem pergunta sempre desce; a resposta é seguida enquanto a pessoa
  // não subiu para ler o histórico.
  const ultimo = c.fio[c.fio.length - 1];
  const assinatura = `${c.fio.length}:${ultimo?.tipo === 'nyta' ? ultimo.texto.length : 0}:${c.pensando?.texto ?? ''}`;
  useEffect(() => {
    const el = rolagem.current;
    if (!el || !c.fio.length) return;
    if (primeira.current) {
      primeira.current = false;
      el.scrollTop = el.scrollHeight;
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
      return;
    }
    if (ultimo?.tipo === 'pessoa') subiu.current = false;
    // A rolagem é a da conversa, e não `scrollIntoView`: aquele rola também a PÁGINA, e na landing
    // (onde a conversa fica no meio dela) cada palavra da resposta puxava a página para a Nyta.
    if (!subiu.current) el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura]);

  // As mais antigas entram por cima sem a leitura pular.
  useEffect(() => {
    const el = rolagem.current;
    if (!el) return;
    const cresceu = el.scrollHeight - alturaAntes.current;
    if (cresceu > 0 && el.scrollTop < 50 && !primeira.current) el.scrollTop += cresceu;
    alturaAntes.current = el.scrollHeight;
  }, [c.fio.length]);

  useEffect(() => {
    const el = rolagem.current;
    const marco = topo.current;
    if (!el || !marco || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && c.temMaisAntigas && !c.carregandoHistorico) c.carregarAntigas();
    }, { root: el, rootMargin: '100px 0px 0px 0px' });
    obs.observe(marco);
    return () => obs.disconnect();
  }, [c.temMaisAntigas, c.carregandoHistorico, c]);

  // Um aviso por vez, o mais grave: o erro vence; o módulo fora do ar volta quando ele sai.
  const avisos = c.aviso ? (
    <InlineNotification kind='error' lowContrast title='A Nyta não respondeu' subtitle={c.aviso} onCloseButtonClick={c.dispensarAviso}
      statusIconDescription={rotuloDoAviso('error')} aria-label={ROTULOS.fecharAviso} className={styles.aviso} />
  ) : c.modulosFora && modulosDispensados !== c.modulosFora ? (
    <InlineNotification kind='warning' lowContrast title='Módulo fora do ar' subtitle={c.modulosFora} onCloseButtonClick={() => setModulosDispensados(c.modulosFora)}
      statusIconDescription={rotuloDoAviso('warning')} aria-label={ROTULOS.fecharAviso} className={styles.aviso} />
  ) : null;

  if (c.vazia) {
    return (
      <div className={`${styles.conversa} ${compacta ? styles.compacta : ''}`}>
        <div className={styles.vazia}>
          {avisos}
          <span className={styles.marcaGrande}><MarcaDaNyta /></span>
          <h2 className={styles.saudacao}>{c.saudacao}</h2>
          <Campo c={c} destaque />
          {!c.limite && (
            <div className={styles.sugestoes} role='group' aria-label='Sugestões de pergunta'>
              {c.sugestoes.map((s) => (
                <Button key={s} kind='tertiary' size='sm' className={styles.sugestao} disabled={!c.podeEscrever} onClick={() => { void c.enviar(s); }}>{s}</Button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.conversa} ${compacta ? styles.compacta : ''}`}>
      <div className={styles.rolagem} ref={rolagem} onScroll={(e) => {
        const el = e.currentTarget;
        subiu.current = el.scrollHeight - el.scrollTop - el.clientHeight > 100;
      }}>
        <div ref={topo} />
        {c.carregandoHistorico && <div className={styles.fio}><SkeletonText paragraph lineCount={3} className={styles.esqueleto} /></div>}
        {/* O log anuncia o que chega; enquanto o histórico carrega (por cima), ele fica mudo. */}
        <div className={styles.fio} role='log' aria-live={c.carregandoHistorico || logMudo ? 'off' : 'polite'} aria-label='Conversa com a Nyta'>
          {c.fio.map((item) => {
            if (item.tipo === 'pessoa') {
              return <div key={item.id} className={styles.pessoa}><p className={styles.bolha}>{item.texto}</p></div>;
            }
            if (item.tipo === 'acao') return <CartaoDaAcao key={item.id} acao={item.acao} />;
            if (item.tipo === 'falhou') {
              return (
                <div key={item.id} className={styles.falha}>
                  <p className={styles.falhou}><WarningAlt aria-hidden />A Nyta não conseguiu completar a resposta.</p>
                  {item.tentar && <div><Button kind='tertiary' size='sm' renderIcon={Renew} disabled={!c.podeEscrever} onClick={item.tentar}>Tentar de novo</Button></div>}
                </div>
              );
            }
            if (item.tipo === 'semTexto') {
              return <p key={item.id} className={styles.falhou}><WarningAlt aria-hidden />Não consegui formular essa resposta direito. Pode reformular o pedido?</p>;
            }
            return (
              <article key={item.id} className={styles.nyta} aria-label='Resposta da Nyta' aria-busy={item.escrevendo}>
                <div className={styles.quemFala}>
                  {/* O "IA" é o avatar da Nyta e o rótulo focado: cada resposta diz que é IA, e explica. */}
                  <RotuloDeIA sobre={explicacaoDa(item.texto, item.anexos, item.leituras)} tamanho='mini' alinhar='bottom-start' />
                  <span className={styles.nome}>Nyta</span>
                </div>
                {item.texto && <FalaDaNyta texto={item.texto} artistId={c.artistId} anexos={item.anexos} />}
                {item.acoes.map((a) => <CartaoDaAcao key={a.id} acao={a} />)}
              </article>
            );
          })}
          {c.pensando && <Pensando texto={c.pensando.texto} />}
        </div>
      </div>
      <div className={styles.rodape}>
        {avisos}
        <Campo c={c} />
      </div>
    </div>
  );
};
